import { useCallback, useEffect, useMemo } from 'react';
import { useStoreValue } from '@/lib/tinyStore';
import { emptySnapshot } from '../presenceAdapter';
import {
  presenceStore, selectSelf, selectSelfState, selectSelfStatus, snapshotOf,
} from '../presenceStore';
import { watchPresence } from '../presenceWatcher';
import type { ActivityState, CustomStatus, PresenceSnapshot, PresenceState } from '../types';

/** The signed-in user's full presence snapshot. */
export function useSelfPresence(): PresenceSnapshot {
  return useStoreValue(presenceStore, selectSelf);
}

/** Just the resolved state — the cheapest subscription for a dot. */
export function useSelfPresenceState(): ActivityState {
  return useStoreValue(presenceStore, selectSelfState);
}

export function useSelfCustomStatus(): CustomStatus | null {
  return useStoreValue(presenceStore, selectSelfStatus);
}

/**
 * Presence for one person. Subscribes on mount and releases on unmount, so a
 * virtualised list only ever watches the rows currently in the viewport.
 */
export function usePersonPresence(userId: string | undefined): ActivityState {
  useEffect(() => {
    if (!userId) return undefined;
    return watchPresence([userId]);
  }, [userId]);

  const selector = useCallback(
    (state: PresenceState): ActivityState =>
      userId ? (snapshotOf(state, userId)?.effectiveState ?? 'offline') : 'offline',
    [userId],
  );

  return useStoreValue(presenceStore, selector);
}

/** Full snapshot for one person (status text, since, …). */
export function usePersonSnapshot(userId: string | undefined): PresenceSnapshot | null {
  useEffect(() => {
    if (!userId) return undefined;
    return watchPresence([userId]);
  }, [userId]);

  const selector = useCallback(
    (state: PresenceState): PresenceSnapshot | null =>
      userId ? snapshotOf(state, userId) : null,
    [userId],
  );

  return useStoreValue(presenceStore, selector);
}

/**
 * Batch variant for rosters. `userIds` must be memoised by the caller —
 * a fresh array every render would re-subscribe every render.
 */
export function useRosterPresence(userIds: readonly string[]): Record<string, PresenceSnapshot> {
  useEffect(() => watchPresence(userIds), [userIds]);

  const selector = useCallback(
    (state: PresenceState): Record<string, PresenceSnapshot> => {
      const out: Record<string, PresenceSnapshot> = {};
      for (const id of userIds) out[id] = snapshotOf(state, id) ?? emptySnapshot(id);
      return out;
    },
    [userIds],
  );

  const equal = useCallback(
    (a: Record<string, PresenceSnapshot>, b: Record<string, PresenceSnapshot>) =>
      userIds.every((id) => a[id] === b[id]),
    [userIds],
  );

  return useStoreValue(presenceStore, selector, equal);
}

/** Stable identity for a list of ids so `useRosterPresence` does not thrash. */
export function usePresenceIds(ids: readonly string[]): readonly string[] {
  const key = ids.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` is the value identity
  return useMemo(() => ids, [key]);
}
